import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ArchiveEntry } from '@the-wall/shared';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { ConfirmModal } from '@/components/ConfirmModal';
import { SEO } from '@/components/SEO';

export function Gallery() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.adminRole === 'ADMIN' || user?.adminRole === 'SUPER_ADMIN';

  const [archives, setArchives] = useState<ArchiveEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [archiveConfirm, setArchiveConfirm] = useState<{
    archive: ArchiveEntry;
    hidden: boolean;
  } | null>(null);
  const [hideLoading, setHideLoading] = useState(false);

  /** Only treat as hidden when API explicitly says true. */
  const isArchiveHidden = (a: ArchiveEntry) => a.hidden === true;

  const loadArchives = useCallback(async () => {
    try {
      const data = await api.getArchives(token);
      setArchives(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load archives');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadArchives();
  }, [loadArchives]);

  const handleHideConfirm = useCallback(async () => {
    if (!archiveConfirm || !token) return;
    setHideLoading(true);
    try {
      await api.hideArchive(token, archiveConfirm.archive.id, archiveConfirm.hidden);
      setArchiveConfirm(null);
      await loadArchives();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update gallery');
    } finally {
      setHideLoading(false);
    }
  }, [archiveConfirm, token, loadArchives]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wall-accent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="bg-red-900/50 border border-red-500 text-red-200 px-6 py-4 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  if (archives.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-wall-muted">
        <svg className="w-24 h-24 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <h2 className="text-xl font-medium mb-2">No archives yet</h2>
        <p>The first cleanup will be revealed when it happens.</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Gallery"
        description="Browse the archive of daily pixel artworks created by The Wall community. Each piece represents a day of collaborative creativity."
        url="/gallery"
      />
      <div className="h-full overflow-auto p-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gradient mb-2">The Permanent Board</h1>
          <p className="text-wall-muted mb-8">
            Finished canvases are saved here forever after each cleanup. Click to explore and
            comment.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archives.map((archive) => (
              <div
                key={archive.id}
                className="group bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-xl overflow-hidden hover:border-wall-accent/50 hover:shadow-glow transition-all duration-300 relative"
              >
                <Link to={`/gallery/${archive.id}`} className="block">
                  <div className="aspect-square bg-wall-bg relative overflow-hidden">
                    <img
                      src={archive.imageUrl}
                      alt={`Canvas from ${archive.date}`}
                      className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      style={{ imageRendering: 'pixelated' }}
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <span className="text-sm font-medium text-white flex items-center gap-1">
                        View Details
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </span>
                    </div>
                    {/* Admin: hide/unhide button */}
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setArchiveConfirm({ archive, hidden: !isArchiveHidden(archive) });
                        }}
                        className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 hover:bg-black/80 text-white text-sm font-medium transition-colors z-10"
                        title={isArchiveHidden(archive) ? 'Unhide gallery' : 'Hide gallery'}
                        aria-label={isArchiveHidden(archive) ? 'Unhide gallery' : 'Hide gallery'}
                      >
                        {isArchiveHidden(archive) ? (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          </svg>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg group-hover:text-wall-accent transition-colors">
                      {new Date(archive.date).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                      {isArchiveHidden(archive) && isAdmin && (
                        <span className="ml-2 text-xs font-normal text-wall-muted">(Hidden)</span>
                      )}
                    </h3>
                    <div className="mt-2 flex flex-wrap gap-4 text-sm text-wall-muted">
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                        </svg>
                        {archive.totalPixels.toLocaleString()} pixels
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                        </svg>
                        {archive.uniqueUsers.toLocaleString()} artists
                      </span>
                      <span className="flex items-center gap-1">
                        <svg
                          className="w-4 h-4 shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                        {archive.commentCount.toLocaleString()}{' '}
                        {archive.commentCount === 1 ? 'comment' : 'comments'}
                      </span>
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>

          <ConfirmModal
            isOpen={!!archiveConfirm}
            title={archiveConfirm?.hidden ? 'Hide gallery' : 'Unhide gallery'}
            message={
              archiveConfirm?.hidden
                ? 'This gallery will be hidden from non-admin users. They will not see it in the list or open it. Are you sure?'
                : 'This gallery will be visible to all users again. Continue?'
            }
            confirmText={archiveConfirm?.hidden ? 'Hide' : 'Unhide'}
            cancelText="Cancel"
            confirmVariant="danger"
            isLoading={hideLoading}
            onConfirm={handleHideConfirm}
            onCancel={() => setArchiveConfirm(null)}
          />
        </div>
      </div>
    </>
  );
}
